import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  parseOrderingErrorBody,
  type TableSortState,
} from '../../utils/listOrdering';
import {
  ADMIN_RESERVATIONS_PAGE_SIZE,
  ADMIN_RESERVATIONS_SORT_ERROR,
  adminReservationsOrdering,
  isAdminReservationSortable,
  nextAdminReservationsOrderingParam,
  readAdminReservationsPage,
  readAdminReservationsSort,
  type AdminReservation,
} from './adminReservationsModel';
import {
  PA_URL_RESERVATIONS,
  type AdminReservationSortKey,
} from './platformAdminOrdering';

export interface AdminReservationsList {
  reservations: AdminReservation[];
  loading: boolean;
  page: number;
  totalPages: number;
  count: number;
  phoneSearch: string;
  debouncedPhoneSearch: string;
  listError: string | null;
  sort: TableSortState<AdminReservationSortKey>;
  onPhoneSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSort: (key: AdminReservationSortKey) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

/**
 * Cross-store reservations list state. Owned by the Platform Admin page (not
 * the tab) so the page, phone search and last rows survive tab switches;
 * it only fetches while `enabled`.
 */
export function useAdminReservationsList(
  enabled: boolean,
): AdminReservationsList {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reservations, setReservations] = useState<AdminReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [debouncedPhoneSearch, setDebouncedPhoneSearch] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  const sort = useMemo(
    () => readAdminReservationsSort(searchParams.get(PA_URL_RESERVATIONS)),
    [searchParams],
  );
  const ordering = useMemo(() => adminReservationsOrdering(sort), [sort]);

  const onSort = useCallback(
    (key: AdminReservationSortKey) => {
      if (!isAdminReservationSortable(key)) return;
      setPage(1);
      setListError(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const param = nextAdminReservationsOrderingParam(
          prev.get(PA_URL_RESERVATIONS),
          key,
        );
        if (param) next.set(PA_URL_RESERVATIONS, param);
        else next.delete(PA_URL_RESERVATIONS);
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedPhoneSearch(phoneSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [phoneSearch]);

  const onPhoneSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneSearch(event.target.value);
    setPage(1);
    setListError(null);
  };

  const fetchReservations = useCallback(
    async (requestedPage = 1) => {
      setLoading(true);
      setListError(null);
      try {
        const response = await api.get(
          apiEndpoints.getAdminReservations(
            requestedPage,
            ADMIN_RESERVATIONS_PAGE_SIZE,
            ordering,
            {
              phone: debouncedPhoneSearch || undefined,
            },
          ),
        );
        if (response && response.ok) {
          const result = readAdminReservationsPage(await response.json());
          setReservations(result.reservations);
          if (result.count !== undefined) setCount(result.count);
          if (result.totalPages !== undefined) {
            setTotalPages(result.totalPages);
          }
        } else if (response?.status === 400) {
          const body = await response.json().catch(() => ({}));
          setListError(
            parseOrderingErrorBody(body) || ADMIN_RESERVATIONS_SORT_ERROR,
          );
          setReservations([]);
          setCount(0);
          setTotalPages(1);
        }
      } catch {
        setReservations([]);
      } finally {
        setLoading(false);
      }
    },
    [ordering, debouncedPhoneSearch],
  );

  useEffect(() => {
    if (enabled) {
      fetchReservations(page);
    }
  }, [enabled, page, fetchReservations]);

  return {
    reservations,
    loading,
    page,
    totalPages,
    count,
    phoneSearch,
    debouncedPhoneSearch,
    listError,
    sort,
    onPhoneSearchChange,
    onSort,
    onPreviousPage: () => setPage((p) => Math.max(p - 1, 1)),
    onNextPage: () => setPage((p) => Math.min(totalPages, p + 1)),
  };
}
